import React, { useContext } from 'react';
import { hexToRgb, darken, lighten, IconButton, Tooltip } from '@mui/material';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { CollisionPriority } from '@dnd-kit/abstract';
import { useSortable } from '@dnd-kit/react/sortable';
import styled from '@emotion/styled';
import { useAppSelector } from '@/redux/hooks';
import { selectGraphsStatus } from '@/redux/selectors/dashboardSelectors';
import { ConfigContext } from '@/contextAPI/configContext';
import { DragTypes } from './WidgetFrame';

type PanelProps = {
  children: React.ReactNode;
  index: number;
  id: string;
  isChildDropTarget: boolean | undefined;
  count: number;
  onDelete: () => void;
};

const Panel = ({ count, children, id, index, isChildDropTarget, onDelete }: PanelProps) => {
  const status = useAppSelector(selectGraphsStatus);
  const dashboardConfig = useContext(ConfigContext)?.dashboardConfig;
  const maxPerRow = dashboardConfig?.maxGraphsPerRow || 3;
  const view = status.editable ? 'editable' : 'read-only';
  const { ref, isDropTarget: isContainerDropTarget } = useSortable({
    id,
    data: {
      type: DragTypes.panel,
      id,
    },
    index,
    disabled: !status.editable,
    type: 'panel',
    collisionPriority: CollisionPriority.Low,
    accept: [DragTypes.panel],
  });
  const showMaxOverlay = (isContainerDropTarget || isChildDropTarget) && count >= maxPerRow;
  const deleteDisabled = count > 0;
  const forbidden = (!!isChildDropTarget || isContainerDropTarget) && count >= maxPerRow;

  return (
    <Wrapper forbidden={forbidden} view={view} className="Column" ref={ref}>
      {showMaxOverlay && (
        <Overlay opacity={0.8}>
          <h3>Max 3 items per row</h3>
        </Overlay>
      )}
      {view === 'editable' && (
        <CrudNav id="crud-nav">
          <StyledDragIcon icon="grip" height={26} />
          <Tooltip title={deleteDisabled ? 'Cannot delete row with widgets in it' : 'Delete'} placement="top-start">
            <span>
              <StyledCloseIconButton onClick={onDelete} disabled={deleteDisabled} size="small">
                <PolarisIcon icon="close" height={26} />
              </StyledCloseIconButton>
            </span>
          </Tooltip>
        </CrudNav>
      )}
      <Sash className={count === 2 ? "staggered-sash" : ""} {...{ view }}>
        {children}
      </Sash>
    </Wrapper>
  );
}

export default Panel;

const Wrapper = styled.div<{ view: 'read-only' | 'editable'; forbidden: boolean; }>`
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 175px;
  min-height: 118px;
  margin-bottom: ${props => props.theme.spacing(3)};

  border: ${({ theme, view, forbidden }) =>
    view === 'editable' ? `2px dashed ${forbidden ? theme.palette.error.dark : theme.palette.default.main}8a` : 'transparent'};
  transition: all 0.2s ease-in-out;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  background-color: ${({ theme, view }) => {
    if (view === 'editable') {
      return darken(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.3 : 0.025);
    } else {
      return 'transparent';
    }
  }};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;

  &:hover {
    background-color: ${({ theme, view }) => (view === 'editable' ? theme.palette.background.paper : 'transparent')};
  }
`;

const Sash = styled.div<{ view: 'read-only' | 'editable'; }>`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing(3)};
  transition: all 0.2s ease-in-out;
  text-align: ${({ view }) => (view === 'editable' ? 'center' : 'left')};
  height: 100%;
  width: 100%;
  position: relative;

  ${({ theme, view }) =>
    view === 'read-only'
      ? `
    column-gap: ${theme.spacing(3)};
    row-gap: ${theme.spacing(3)};
  `
      : `
    padding: ${theme.spacing(0, 3)};
    padding-bottom: ${theme.spacing(3)};
    > .pane {
      margin: ${theme.spacing(0, 1.5)};
    }
  `}

  > .pane > div {
    left: auto !important;
    top: auto !important;
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    flex-direction: row;
  }
`;

const CrudNav = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: ${({ theme }) => theme.spacing(0.5, 1)};
  column-gap: ${({ theme }) => theme.spacing(0.5)};
  align-items: center;
`;

const StyledDragIcon = styled(PolarisIcon)`
  color: ${({ theme }) => theme.palette.default.main};

  &:hover {
    color: ${({ theme }) => lighten(theme.palette.default.main, 0.5)};
  }
`;

const StyledCloseIconButton = styled(IconButton)`
  color: ${({ theme }) => theme.palette.default.main};

  &:hover {
    color: ${({ theme }) => theme.palette.error.main};
  }
`;

const Overlay = styled.div<{ opacity: number }>`
  background-color: ${({ theme, opacity }) => hexToRgb(theme.palette.background.default).replace(')', `, ${opacity})`).replace('rgb', 'rgba')};
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9;
`;